import { useState, useEffect, useCallback } from "react";
import { post } from "../api";
import { useDashboard } from "../context/DashboardContext";

function filterBody(filters: { accountIds: string[]; services: string[]; regions: string[] }): Record<string, any> {
  const body: Record<string, any> = {};
  if (filters.accountIds.length) body.account_ids = filters.accountIds;
  if (filters.services.length) body.services = filters.services;
  if (filters.regions.length) body.regions = filters.regions;
  return body;
}

export function useVistaQuery<T = any>(
  endpoint: string,
  extraParams?: Record<string, any>,
  deps?: any[]
) {
  const { customer, dateParams, filters } = useDashboard();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filterKey = JSON.stringify(filters);
  const dateKey = JSON.stringify(dateParams);

  const fetch = useCallback(async () => {
    if (!customer) return;
    // Need either month or start_date+end_date
    if (!dateParams.month && !dateParams.start_date) return;
    setLoading(true);
    setError(null);
    try {
      const body = { customer, ...dateParams, ...extraParams, ...filterBody(filters) };
      const result = await post<T>(endpoint, body);
      setData(result);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [customer, dateKey, filterKey, endpoint, ...(deps ?? [])]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

export function useVistaTrend<T = any>(months: number = 6) {
  const { customer, filters } = useDashboard();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);

  const filterKey = JSON.stringify(filters);

  useEffect(() => {
    if (!customer) return;
    setLoading(true);
    const body = { customer, months, ...filterBody(filters) };
    post<T>("/api/query/trend", body)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [customer, months, filterKey]);

  return { data, loading };
}
